// Money display for NPR (DESIGN_SYSTEM §4.7, D-013). Storage and the API use integer paisa
// (1 NPR = 100 paisa; API_CONTRACTS §1.2). These helpers never use `style: "currency"` (it prints
// "NPR 1,15,000.00") nor `compactDisplay: "long"` (wrong by 10x for ne-NP): we format the number,
// then prefix "Rs" ourselves, and build lakh/crore compaction by hand.
import { groupIndian, normalizeDigits } from "./digits";

export const CURRENCY_LABEL = "Rs";
/** U+00A0: "Rs" never wraps away from the number. */
export const NBSP = "\u00A0";
/** U+2212: the true minus sign for negative amounts. */
export const MINUS = "\u2212";

const LAKH = 100_000; // rupees
const CRORE = 10_000_000; // rupees

export interface MoneyFormatOptions {
  /**
   * "auto" (lists): decimals only when non-zero. "always" (invoices, receipts): 2 decimals.
   * @default "auto"
   */
  decimals?: "auto" | "always";
  /** Omit the "Rs" label (e.g. inside a field that already shows the prefix). */
  bare?: boolean;
}

function assertPaisa(paisa: number): void {
  if (!Number.isSafeInteger(paisa)) throw new RangeError(`paisa must be a safe integer: ${paisa}`);
}

/** "1,15,000" or "1,15,000.50" for a non-negative paisa amount. */
function absoluteNumber(paisa: number, decimals: "auto" | "always"): string {
  const abs = Math.abs(paisa);
  const rupees = Math.floor(abs / 100);
  const fraction = abs % 100;
  const whole = groupIndian(String(rupees));
  if (decimals === "always" || fraction !== 0) {
    return `${whole}.${String(fraction).padStart(2, "0")}`;
  }
  return whole;
}

/**
 * Formats integer paisa as house-style money: `Rs 1,15,000`, `Rs 1,15,000.50`, `\u2212Rs 500`.
 * Callers must pair a money value with its state in operational views (DESIGN_SYSTEM §2 principle 1).
 */
export function formatMoney(paisa: number, options: MoneyFormatOptions = {}): string {
  assertPaisa(paisa);
  const num = absoluteNumber(paisa, options.decimals ?? "auto");
  const body = options.bare ? num : `${CURRENCY_LABEL}${NBSP}${num}`;
  return paisa < 0 ? `${MINUS}${body}` : body;
}

function trimDecimals(value: number, max: number): string {
  const fixed = value.toFixed(max);
  return fixed.replace(/\.?0+$/, "");
}

/**
 * Compact form for KPI tiles only: `Rs 1.15 lakh`, `Rs 2.4 crore`. Below one lakh it returns the
 * full form. Never "115K" or "1.2M".
 */
export function formatMoneyCompact(paisa: number): string {
  assertPaisa(paisa);
  const rupees = Math.abs(paisa) / 100;
  const sign = paisa < 0 ? MINUS : "";
  if (rupees < LAKH) return formatMoney(paisa);
  let unit = rupees >= CRORE ? "crore" : "lakh";
  let value = rupees / (unit === "crore" ? CRORE : LAKH);
  // 99,99,999 rupees rounds to "100 lakh": promote to crore instead.
  if (unit === "lakh" && Number(value.toFixed(2)) >= 100) {
    unit = "crore";
    value = rupees / CRORE;
  }
  const grouped = trimDecimals(value, 2);
  const [intPart = "0", frac] = grouped.split(".");
  const num = frac ? `${groupIndian(intPart)}.${frac}` : groupIndian(intPart);
  return `${sign}${CURRENCY_LABEL}${NBSP}${num} ${unit}`;
}

// ---------------------------------------------------------------- words (English, Indian system)
const ONES = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

function below100(n: number): string {
  if (n < 20) return ONES[n] ?? "";
  const t = TENS[Math.floor(n / 10)] ?? "";
  const o = n % 10;
  return o ? `${t}-${ONES[o]}` : t;
}
function below1000(n: number): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  if (!h) return below100(r);
  return r ? `${ONES[h]} hundred ${below100(r)}` : `${ONES[h]} hundred`;
}

/** Integer rupees in words with the Indian system: "one lakh fifteen thousand". */
export function rupeesInWords(rupees: number): string {
  if (!Number.isSafeInteger(rupees) || rupees < 0) throw new RangeError("rupees must be >= 0");
  if (rupees === 0) return "zero";
  const parts: string[] = [];
  const crore = Math.floor(rupees / CRORE);
  let rest = rupees % CRORE;
  if (crore) parts.push(`${crore >= 100 ? rupeesInWords(crore) : below100(crore)} crore`);
  const lakh = Math.floor(rest / LAKH);
  rest %= LAKH;
  if (lakh) parts.push(`${below100(lakh)} lakh`);
  const thousand = Math.floor(rest / 1000);
  rest %= 1000;
  if (thousand) parts.push(`${below100(thousand)} thousand`);
  if (rest) parts.push(below1000(rest));
  return parts.join(" ");
}

/**
 * "One lakh fifteen thousand rupees and fifty paisa". Shown under money inputs for amounts of one
 * lakh or more, to catch extra-zero mistakes (DESIGN_SYSTEM §9.3).
 */
export function amountInWords(paisa: number): string {
  assertPaisa(paisa);
  const abs = Math.abs(paisa);
  const rupees = Math.floor(abs / 100);
  const p = abs % 100;
  let text = `${rupeesInWords(rupees)} ${rupees === 1 ? "rupee" : "rupees"}`;
  if (p) text += ` and ${below100(p)} paisa`;
  if (paisa < 0) text = `minus ${text}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Accessible name for a money value: "1 lakh 15 thousand rupees". Screen readers otherwise misread
 * lakh grouping (DESIGN_SYSTEM §10.3).
 */
export function moneySpoken(paisa: number): string {
  assertPaisa(paisa);
  const abs = Math.abs(paisa);
  let rupees = Math.floor(abs / 100);
  const p = abs % 100;
  const parts: string[] = [];
  const crore = Math.floor(rupees / CRORE);
  rupees %= CRORE;
  const lakh = Math.floor(rupees / LAKH);
  rupees %= LAKH;
  const thousand = Math.floor(rupees / 1000);
  rupees %= 1000;
  if (crore) parts.push(`${crore} crore`);
  if (lakh) parts.push(`${lakh} lakh`);
  if (thousand) parts.push(`${thousand} thousand`);
  if (rupees || parts.length === 0) parts.push(String(rupees));
  let text = `${parts.join(" ")} rupees`;
  if (p) text += ` ${p} paisa`;
  return paisa < 0 ? `minus ${text}` : text;
}

// ---------------------------------------------------------------- input
export type MoneyParseError = "empty" | "invalid" | "too_many_decimals" | "negative" | "too_large";
export type MoneyParseResult = { ok: true; paisa: number } | { ok: false; error: MoneyParseError };

/** Strips the label, grouping separators and spaces; normalises Devanagari digits. */
function cleanMoneyText(input: string): string {
  return normalizeDigits(input)
    .replace(/(Rs\.?|रु|रू|NPR)/gi, "")
    .replace(/[\s,\u00A0]/g, "")
    .replace(/\u2212/g, "-");
}

/** Parses what a person typed ("1,15,000.5", "११५०००", "Rs 2400") into integer paisa. */
export function parseMoney(
  input: string,
  options: { allowNegative?: boolean } = {},
): MoneyParseResult {
  const text = cleanMoneyText(input);
  if (text === "" || text === "-") return { ok: false, error: "empty" };
  const m = /^(-?)(\d*)(?:\.(\d*))?$/.exec(text);
  if (!m || (m[2] === "" && (m[3] ?? "") === "")) return { ok: false, error: "invalid" };
  const [, neg, whole = "", frac = ""] = m;
  if (frac.length > 2) return { ok: false, error: "too_many_decimals" };
  if (neg && !options.allowNegative) return { ok: false, error: "negative" };
  const paisa = Number(whole || "0") * 100 + Number(frac.padEnd(2, "0") || "0");
  if (!Number.isSafeInteger(paisa)) return { ok: false, error: "too_large" };
  return { ok: true, paisa: neg ? -paisa : paisa };
}

/**
 * Live lakh grouping while typing: keeps a trailing "." and partial decimals, so "115000." stays
 * editable as "1,15,000.". Returns the cleaned text unchanged when it is not a number yet.
 */
export function formatMoneyInput(input: string): string {
  const text = cleanMoneyText(input);
  const m = /^(-?)(\d*)(\.?)(\d{0,2})/.exec(text);
  if (!m) return "";
  const [, neg = "", whole = "", dot = "", frac = ""] = m;
  const grouped = whole ? groupIndian(whole) : dot ? "0" : "";
  return `${neg}${grouped}${dot}${frac}`;
}
